import { useStripe } from "@stripe/react-stripe-js";
import { useRef } from "react";
import type { WrappedComponentProps } from "react-intl";
import {
  createPaymentSourceBankAuthorize,
  createPaymentSourceCard,
  createPaymentSourceCardSetup,
  createPaymentSourceInvoice,
  createPaymentSourcePaypalAuthorize,
  updateCustomerAddress,
  updateCustomerVat,
} from "src/billinglatest/clients/service.client";
import { useError } from "src/billinglatest/hooks/useError";
import { useLoader } from "src/billinglatest/hooks/useLoader";
import { getHostWithPath } from "src/billinglatest/util/url";
import { setSafeLocationHref } from "src/helpers/urlHelper";
import { useAppContext } from "src/hooks/useAppContext";
import type {
  BillingCycle,
  CurrencyCode,
  PlanId,
} from "../PlanBillingStep/constants";
import type {
  PixiAddress,
  PixiPaymentMethod,
} from "../../hooks/usePixiWizardState";
import { finalizePurchase, type PixiPurchaseOrder } from "./finalizePurchase";
import {
  PIXI_RETURN_PATHS,
  savePurchaseResume,
  type PixiPurchaseProvider,
} from "./purchaseResume";
import { pixiAddressToBillingAddress } from "./mappers/checkoutPayload";

/** The intl object supplied by `injectIntl` (react-intl v2 has no `useIntl`). */
type Intl = WrappedComponentProps["intl"];

export interface PurchaseInput {
  /** Plan tier group, e.g. "core" | "pro". */
  planGroup: string;
  /** Billing cycle, "year" | "month". */
  billingCycle: string;
  /**
   * The trialist's locked billing currency, resolved upstream (useTrialistCurrency)
   * and seeded into the wizard. Chargebee locks currency to the customer, so this
   * is authoritative — there is NO fallback: if it's missing, the purchase fails
   * rather than charging a currency the customer never agreed to.
   */
  currency: string;
  licenses: number;
  /** Selected payment method; only "credit_card" runs card setup + 3DS/SCA. */
  paymentMethod?: PixiPaymentMethod;
  /** Stripe paymentMethod id (pm_…) captured on the Payment step; card only. */
  cardPaymentMethodId?: string;
  /** Billing address to persist before subscribing — Chargebee requires one. */
  billingAddress: PixiAddress;
  /**
   * VAT number to submit for Chargebee validation against the billing country.
   * Optional: only sent when non-empty. An invalid VAT aborts the purchase
   * before any charge (GRWBKK-1704).
   */
  vatNumber?: string;
  /**
   * PO number applied to invoices. Optional and unvalidated — submitted only
   * after the purchase succeeds, best-effort, so it can never undo a completed
   * purchase (GRWBKK-1704).
   */
  poNumber?: string;
  /**
   * Set for the free-device deal: activates the subscription as a PIXI bundle
   * (adds the free device + paid shipping). The backend resolves the shipping
   * cost from its rate table using the address country + service, so the FE
   * sends only the destination and the service level — no amount, no item id.
   */
  bundle?: {
    shippingAddress: PixiAddress;
    shippingService: string;
  };
}

/**
 * Outcome of a purchase submit.
 * "success": card/invoice completed the trial → paid conversion inline.
 * "redirected": a redirect-based provider (PayPal / Direct Debit) kicked off its gateway round-trip; the page is now navigating away.
 * "failed": a handled failure; the message is surfaced via `error`.
 */
export type PurchaseResult = "success" | "redirected" | "failed";

export interface UsePurchase {
  /** Submits the order; see {@link PurchaseResult} for the outcomes. */
  purchase: (input: PurchaseInput) => Promise<PurchaseResult>;
  loading: boolean;
  /**
   * Staged progress copy for the current step of the purchase chain (empty when
   * idle). Mirrors the old billing modal's loader, which swaps a single overlay
   * message before each API call (GRWBKK-1737).
   */
  message: string;
  /** Latest error message, surfaced from the charge steps (empty if none). */
  error: string;
}

/**
 * Orchestrates the Pixi Bundle purchase submit.
 *
 * This mirrors how the billing-latest page converts a NON-subscribed (trial)
 * user to paid — the trial org is already a Chargebee customer, so we do NOT
 * create one; we run the same SDK sequence as `Manage` → the form hooks:
 *
 * Step 1 — persist the billing address (updateCustomerAddress).
 * Step 2 — attach the payment method (card: setup + 3DS + attach, or invoice).
 * Step 3 — set the chosen plan + licences (updateSubscription), using the
 * trialist's locked currency passed in (no fallback).
 * Step 4 — activate the trial → paid (startSubscription). For the free-device
 * deal this passes the PIXI bundle option + shipping destination, so the backend
 * adds the free device and charges the shipping it resolves from its rate table.
 *
 * The card is authenticated once, on the SetupIntent in step 2; the plan charge
 * is never pre-authorised, so step 4 is the only charge. A browser-side pre-auth
 * cannot cover a bundle invoice anyway: it can only quote plan + licences, while
 * the invoice also includes the shipping the backend resolves at activation, so
 * its authorisation would sit uncaptured on the customer's card.
 */
export const usePurchase = (intl: Intl): UsePurchase => {
  const stripe = useStripe();
  const loader = useLoader();
  const error = useError();
  const context = useAppContext();

  /**
   * The card token (pm_…) already attached to the customer this session, so a
   * retry after a later-step failure (e.g. an unsupported coupon at
   * startSubscription) skips re-attaching it. Chargebee/Stripe bind the pm to
   * the customer during card setup, so re-attaching the same pm throws
   * "payment method … belongs to the Customer …". A different token (the user
   * went back and re-entered a card) won't match, so that card still attaches.
   */
  const attachedCardId = useRef<string | undefined>(undefined);

  const t = (id: string, defaultMessage: string) =>
    intl.formatMessage({ id, defaultMessage });

  /**
   * Update the shared loader's overlay message for the current purchase stage.
   * Reuses the old billing modal's staged-message pattern (GRWBKK-1737).
   */
  const stage = (id: string, defaultMessage: string) =>
    loader.setMessage(t(id, defaultMessage));

  /**
   * Attach the Stripe-tokenised card (pm_…) to Chargebee, completing any 3DS on
   * the card setup. Mirrors `usePaymentMethodForm.saveCard` (the pm id was
   * already produced on the Payment step, so we skip re-tokenising here).
   */
  const attachCard = async (paymentMethodId: string, spaceId?: string) => {
    if (!stripe) {
      throw new Error(
        t(
          "pixi_wizard.checkout.error.payment_init",
          "Could not initialise the payment gateway. Please try again.",
        ),
      );
    }
    const intent = await createPaymentSourceCardSetup(
      { paymentMethodId },
      spaceId,
    );
    if (!intent?.id) {
      throw new Error(
        t(
          "pixi_wizard.checkout.error.card_validate",
          "There was a problem validating your card. Please try again.",
        ),
      );
    }
    if (intent.status !== "succeeded" && intent.secret) {
      const confirmed = await stripe.confirmCardSetup(intent.secret);
      if (confirmed.error?.message) {
        throw new Error(confirmed.error.message);
      }
    }
    await createPaymentSourceCard({ token: intent.id }, spaceId);
  };

  const purchase = async ({
    planGroup,
    billingCycle,
    currency,
    licenses,
    paymentMethod,
    cardPaymentMethodId,
    billingAddress,
    vatNumber,
    poNumber,
    bundle,
  }: PurchaseInput): Promise<PurchaseResult> => {
    loader.start();
    error.clear();
    const spaceId = context.currentSpace?.id;
    try {
      const isCard = paymentMethod === "credit_card";

      // Preconditions — validated BEFORE any network call or gateway redirect so
      // a missing org or currency fails fast in-app. The redirect providers
      // (PayPal / Direct Debit) can't recover once they've handed off, so a
      // doomed order must never leave for the gateway.
      const orgId = context.currentOrg?.id;
      if (!orgId) {
        error.setMessage(
          t(
            "pixi_wizard.checkout.purchase_error",
            "Transaction failed. Please try again in a moment.",
          ),
        );
        return "failed";
      }
      // No currency fallback: fail rather than charge a currency the customer
      // never agreed to (mirrors finalizePurchase's own guard). Applied up here
      // so the redirect round-trip and the card 3DS/SCA challenge never run on an
      // unconfirmed currency.
      if (!currency) {
        error.setMessage(
          t(
            "pixi_wizard.checkout.error.currency_missing",
            "We couldn't confirm your billing currency. Please try again.",
          ),
        );
        return "failed";
      }

      // 1. Persist the billing address (Chargebee needs one to charge).
      stage(
        "pixi_wizard.checkout.loading.saving_details",
        "Saving your billing details",
      );
      await updateCustomerAddress(
        pixiAddressToBillingAddress(billingAddress),
        spaceId,
      );

      // 1b. Submit the VAT number for Chargebee to validate against the billing
      // country. Do this BEFORE any charge (and before a redirect provider hands
      // off) so an invalid VAT aborts (throws → caught below → error surfaced,
      // "failed" returned) with no charge taken. Only sent when provided;
      // Chargebee is the source of truth for validity.
      if (vatNumber?.trim()) {
        await updateCustomerVat({ vatNumber: vatNumber.trim() }, spaceId);
      }

      // Redirect-based providers (PayPal / Direct Debit) can't finalize inline:
      // authorize with the gateway, save a resume marker with the order snapshot,
      // then hand off to the gateway. The return path resumes finalizePurchase.
      if (paymentMethod === "paypal" || paymentMethod === "direct_debit") {
        const provider: PixiPurchaseProvider = paymentMethod;
        const returnUrl = getHostWithPath(PIXI_RETURN_PATHS[provider]);

        stage(
          provider === "paypal"
            ? "pixi_wizard.checkout.loading.redirecting_paypal"
            : "pixi_wizard.checkout.loading.redirecting_bank",
          provider === "paypal"
            ? "Redirecting you to PayPal"
            : "Redirecting you",
        );

        let redirectUrl: string | undefined;
        let token: string | undefined;
        if (provider === "paypal") {
          const response = await createPaymentSourcePaypalAuthorize(
            { returnUrl, cancelUrl: `${returnUrl}?cancelled=1` },
            spaceId,
          );
          redirectUrl = response?.redirectUrl;
          token = response?.token;
        } else {
          const response = await createPaymentSourceBankAuthorize(
            { returnUrl },
            spaceId,
          );
          redirectUrl = response?.redirectUrl;
          token = response?.redirectFlowId;
        }

        if (!redirectUrl || !token) {
          error.setMessage(
            t(
              "pixi_wizard.checkout.purchase_error",
              "Transaction failed. Please try again in a moment.",
            ),
          );
          return "failed";
        }

        // Snapshot the order so the return path can resume the same purchase.
        // The PO number rides on the order so the redirect-resume path applies
        // it in finalizePurchase, exactly as the inline path does.
        const order: PixiPurchaseOrder = {
          planGroup: planGroup as PlanId,
          billingCycle: billingCycle as BillingCycle,
          currency: currency as CurrencyCode,
          licenses,
          bundle,
          poNumber,
        };
        savePurchaseResume({
          provider,
          token,
          orgId,
          order,
        });

        setSafeLocationHref(redirectUrl);
        return "redirected";
      }

      // 2. Attach the payment method.
      if (isCard) {
        if (!cardPaymentMethodId) {
          throw new Error(
            t(
              "pixi_wizard.checkout.error.payment_incomplete",
              "Please complete your payment details.",
            ),
          );
        }
        // Skip the attach when this exact card was already attached earlier in
        // the session (a retry after a later step failed). Re-attaching the
        // same pm throws "belongs to the Customer"; a new token still attaches.
        if (attachedCardId.current !== cardPaymentMethodId) {
          stage(
            "pixi_wizard.checkout.loading.setting_up_payment",
            "Setting up your payment method",
          );
          await attachCard(cardPaymentMethodId, spaceId);
          attachedCardId.current = cardPaymentMethodId;
        }
      } else if (paymentMethod === "invoice") {
        await createPaymentSourceInvoice(spaceId);
      } else {
        throw new Error(
          t(
            "pixi_wizard.checkout.error.payment_incomplete",
            "Please complete your payment details.",
          ),
        );
      }

      // 3–4. Set the chosen plan + licences and activate the trial → paid
      // subscription. Extracted into the pure `finalizePurchase` module so the
      // redirect-based providers (PayPal / Direct Debit) can resume the same
      // steps after their gateway round-trip.
      // The optional PO number rides on the order; finalizePurchase applies it
      // best-effort as its last step (step 6), so the inline and redirect-resume
      // paths handle it identically.
      const order: PixiPurchaseOrder = {
        planGroup: planGroup as PlanId,
        billingCycle: billingCycle as BillingCycle,
        currency: currency as CurrencyCode,
        licenses,
        bundle,
        poNumber,
      };
      stage(
        "pixi_wizard.checkout.loading.activating",
        "Activating your subscription",
      );
      await finalizePurchase(order, {
        spaceId,
        currencyMissingMessage: t(
          "pixi_wizard.checkout.error.currency_missing",
          "We couldn't confirm your billing currency. Please try again.",
        ),
      });

      return "success";
    } catch (e) {
      error.setMessage(
        e instanceof Error && e.message
          ? e.message
          : t(
              "pixi_wizard.checkout.purchase_error",
              "Something went wrong. Please try again.",
            ),
      );
      return "failed";
    } finally {
      loader.done();
    }
  };

  return {
    purchase,
    loading: loader.isLoading,
    message: loader.message,
    error: error.message,
  };
};
